import { useCallback } from "react";

import {
    capVisibleBrands,
    recolorPreservingCoreAverage,
    updateBrandsVisibility,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/shared/coreCompetitorsUtils";
import { allTrackers } from "app/services/track/track";

import { MAX_SELECTED_BRANDS } from "../consts";
import type { EViewMode, SeriesData, SeriesItem } from "../types";

interface UseLegendSelectionParams<T extends Partial<Record<EViewMode, SeriesData>> = Record<EViewMode, SeriesData>> {
    topicFilteredData: SeriesItem[];
    selectedTopic: string;
    selectedViewMode: EViewMode;
    setTransformedData: React.Dispatch<React.SetStateAction<T | undefined>>;
}

export const useLegendSelection = <T extends Partial<Record<EViewMode, SeriesData>> = Record<EViewMode, SeriesData>>({
    topicFilteredData,
    selectedTopic,
    selectedViewMode,
    setTransformedData,
}: UseLegendSelectionParams<T>) => {
    const onLegendSelection = useCallback(
        (legendItems: SeriesItem[]): void => {
            const selectedIds = new Set(legendItems.map((item) => item.id));
            const trackableTopicBrands = topicFilteredData.filter((item) => !item.isCoreAverage);
            const prevIds = new Set(trackableTopicBrands.filter((b) => b.visible).map((b) => b.id));
            const enabledBrandId = [...selectedIds].find((id) => !prevIds.has(id));
            const disabledBrandId = [...prevIds].find((id) => !selectedIds.has(id));

            if (enabledBrandId) {
                const item = legendItems.find((legend) => legend.id === enabledBrandId);
                if (item) {
                    allTrackers.dispatchTrackEvent(
                        "Graph Filter",
                        "Brands",
                        `${item.name}/${(item.total * 100).toFixed(0)}%/add`,
                    );
                }
            } else if (disabledBrandId) {
                const item = topicFilteredData.find((legend) => legend.id === disabledBrandId);
                if (item) {
                    allTrackers.dispatchTrackEvent(
                        "Graph Filter",
                        "Brands",
                        `${item.name}/${(item.total * 100).toFixed(0)}%/remove`,
                    );
                }
            }

            setTransformedData((prev) => {
                if (!prev) {
                    return prev;
                }

                const currentViewData = prev[selectedViewMode];
                if (!currentViewData) {
                    return prev;
                }

                const changedBrands = new Map(
                    trackableTopicBrands
                        .filter((item) => prevIds.has(item.id) !== selectedIds.has(item.id))
                        .map((item) => [item.name, selectedIds.has(item.id)]),
                );

                const updatedViewData = Object.fromEntries(
                    Object.entries(currentViewData).map(([topicName, brands]) => {
                        const isCurrentTopic = topicName === selectedTopic;
                        const updatedBrands = updateBrandsVisibility(brands, {
                            isCurrentTopic,
                            selectedIds,
                            changedBrands,
                        });
                        const limitedBrands = capVisibleBrands(updatedBrands, MAX_SELECTED_BRANDS);

                        return [topicName, recolorPreservingCoreAverage(limitedBrands)];
                    }),
                );

                return {
                    ...prev,
                    [selectedViewMode]: updatedViewData,
                };
            });
        },
        [topicFilteredData, selectedTopic, selectedViewMode, setTransformedData],
    );

    return { onLegendSelection };
};
